import type { CreditRateVersion } from './creditRates';

const TOKENS_PER_MILLION = 1_000_000;

const INTERNAL_DECIMAL_PLACES = 6;
const INTERNAL_PRECISION = 10 ** INTERNAL_DECIMAL_PLACES;

type CreditRateSelection =
  | Readonly<{
      status: 'selected';
      rateVersion: CreditRateVersion;
    }>
  | Readonly<{
      status: 'invalid';
      requestedVersion: string;
    }>;

const roundToInternalPrecision = (value: number): number =>
  Math.round((value + Number.EPSILON) * INTERNAL_PRECISION) / INTERNAL_PRECISION;

export const calculateTokensPerCredit = (creditsPerMillionTokens: number): number => {
  if (!Number.isFinite(creditsPerMillionTokens) || creditsPerMillionTokens <= 0) {
    throw new RangeError('Credits per million tokens must be a positive finite number.');
  }

  return roundToInternalPrecision(TOKENS_PER_MILLION / creditsPerMillionTokens);
};

export const getLatestCreditRateVersion = (versions: readonly CreditRateVersion[]): CreditRateVersion => {
  if (versions.length === 0) throw new Error('At least one AI credit-rate version is required.');

  return versions.reduce((latest, candidate) => (candidate.version > latest.version ? candidate : latest));
};

export const selectCreditRateVersion = (
  requestedVersion: string | null,
  versions: readonly CreditRateVersion[]
): CreditRateSelection => {
  const latestVersion = getLatestCreditRateVersion(versions);

  if (requestedVersion === null) {
    return { status: 'selected', rateVersion: latestVersion };
  }

  if (!/^\d+$/.test(requestedVersion)) return { status: 'invalid', requestedVersion };

  const parsedVersion = Number(requestedVersion);
  if (!Number.isSafeInteger(parsedVersion)) return { status: 'invalid', requestedVersion };

  const rateVersion = versions.find(({ version }) => version === parsedVersion);
  if (!rateVersion) return { status: 'invalid', requestedVersion };

  return { status: 'selected', rateVersion };
};
